/* =========================================================================
   Store Locator — ilva-theme-enterprise
   Storefront API client, map adapters, opening hours, stock badges,
   StoreLocatorDrawer + StoreLocatorPage web components.
   ========================================================================= */

/* Loaded by main-product, featured-product and the store-locator page — two
   of them can share a page, each emitting its own script tag, and both
   execute (caching dedupes the download, not the run). The top-level class/
   const declarations would throw "already been declared" on the second run,
   so the whole body is guarded; the drawer element doubles as the
   "already ran" marker. Body indentation deliberately left flat. */
if (!customElements.get('store-locator-drawer')) {

/* --- Storefront API Client --- */

class StorefrontClient {
  constructor(shopDomain, token) {
    this.endpoint = `https://${shopDomain}/api/2025-01/graphql.json`;
    this.token = token;
  }

  async query(graphql, variables = {}) {
    const res = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({ query: graphql, variables }),
    });
    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join(', '));
    return json.data;
  }

  async fetchVariantAvailability(variantGid) {
    const data = await this.query(VARIANT_AVAILABILITY_QUERY, { variantId: variantGid });
    // No node = the variant id didn't resolve. Report zero stores rather than
    // pairing an empty numerator with a real-looking denominator.
    if (!data?.node) return { stores: [], totalStores: 0, quantityAvailable: 0, centrallagerInStock: null };

    const displayedInStores = parseDisplayedInStores(data.node.displayedInStores?.value);
    const siblingVariants = await this.fetchRemainingProductVariants(data.node.product);
    const otherVariantsDisplayedInStores = collectOtherVariantsDisplayedInStores(data.node.id, siblingVariants);

    // storeAvailability omits locations that don't stock the variant, so the row
    // set is built from the full locations list (both are pickup-enabled only)
    // and availability is merged in per location id. Missing entry = variant not
    // available there. This keeps the drawer list and the "in stock in X of Y
    // stores" summary describing the same Y stores.
    const availabilityByLocation = new Map(
      (data.node.storeAvailability?.nodes || []).map((node) => [node.location?.id, node])
    );
    const stores = (data.locations?.nodes || []).map((loc) => {
      const availability = availabilityByLocation.get(loc.id);
      const store = normalizeLocationNode(loc);
      return {
        ...store,
        available: availability?.available || false,
        quantityAvailable: availability?.quantityAvailable ?? 0,
        pickUpTime: availability?.pickUpTime || '',
        onDisplay: displayedInStores[store.axLocationId] === true,
        onDisplayOtherVariant: otherVariantsDisplayedInStores.has(store.axLocationId),
      };
    });
    stores.sort((a, b) => a.name.localeCompare(b.name, 'da'));
    // Sourced from the same response as storeAvailability above, so the delivery
    // estimate's warehouse-vs-retail split reads both halves atomically instead of
    // pairing this live figure with a Liquid-rendered inventory_quantity snapshot
    // taken at a different instant (the cause of the post-purchase state flip). The
    // Centrallager flag rides in the same response for the same reason.
    return {
      stores,
      totalStores: stores.length,
      quantityAvailable: data.node.quantityAvailable ?? 0,
      centrallagerInStock: deliveryParseWarehouseFlag(data.node.centrallagerInStock),
    };
  }

  // The Storefront API caps a connection page at 250, and the catalog has
  // products above that, so the first page rides along with the availability
  // query and any further pages are followed here. Products at or below 250
  // variants never pay the extra round trips.
  async fetchRemainingProductVariants(product) {
    const variants = [...(product?.variants?.nodes || [])];
    let pageInfo = product?.variants?.pageInfo;
    while (pageInfo?.hasNextPage && pageInfo.endCursor) {
      const data = await this.query(PRODUCT_VARIANTS_DISPLAY_QUERY, {
        productId: product.id,
        cursor: pageInfo.endCursor,
      });
      const page = data?.product?.variants;
      if (!page) break;
      variants.push(...(page.nodes || []));
      pageInfo = page.pageInfo;
    }
    return variants;
  }

  async fetchLocations(near) {
    const gql = near ? LOCATIONS_NEAR_QUERY : LOCATIONS_QUERY;
    const variables = near ? { near } : {};
    const data = await this.query(gql, variables);
    if (!data?.locations?.nodes) return [];
    return data.locations.nodes.map(normalizeLocationNode);
  }

  // Batched sibling of fetchVariantAvailability: one round trip for many variants
  // (the fabric swatch modal needs a warehouse verdict per swatch, not just the one
  // current PDP variant). Returns Map<variantGid, boolean> -- whether the central
  // warehouse holds stock, by the same deliveryWarehouseInStock rule the PDP uses, with
  // the warehouse's own location excluded the same way DeliveryEstimate excludes it.
  // The Centrallager flag, quantityAvailable and the shop rows all come from this one
  // response, never from a page-load snapshot, so every input describes the same instant.
  async fetchVariantsAvailability(variantGids, warehouseAxId) {
    if (!variantGids.length) return new Map();
    const data = await this.query(VARIANTS_AVAILABILITY_QUERY, { ids: variantGids });
    const axLocationById = new Map(
      (data.locations?.nodes || []).map((loc) => [loc.id, loc.axLocationId?.value || null])
    );
    const result = new Map();
    (data.nodes || []).forEach((node) => {
      if (!node?.id) return;
      const storeQuantities = (node.storeAvailability?.nodes || [])
        .filter((n) => {
          const axId = axLocationById.get(n.location?.id) || null;
          return !warehouseAxId || axId !== warehouseAxId;
        })
        .map((n) => n.quantityAvailable || 0);
      result.set(
        node.id,
        deliveryWarehouseInStock(
          node.quantityAvailable ?? 0,
          storeQuantities,
          deliveryParseWarehouseFlag(node.centrallagerInStock)
        )
      );
    });
    return result;
  }
}

/* --- Shared availability cache ---
   The stock summary card and the store locator drawer need the same data for the
   same variant, so they share one in-flight request. Rejections are evicted, or a
   single network blip would poison the drawer for that variant for the whole session. */

const availabilityCache = new Map();

function fetchVariantAvailabilityCached(shopDomain, token, variantGid) {
  const key = `${shopDomain}|${variantGid}`;
  if (!availabilityCache.has(key)) {
    const client = new StorefrontClient(shopDomain, token);
    availabilityCache.set(
      key,
      client.fetchVariantAvailability(variantGid).catch((err) => {
        availabilityCache.delete(key);
        throw err;
      })
    );
  }
  return availabilityCache.get(key);
}

const variantsAvailabilityCache = new Map();

function fetchVariantsAvailabilityCached(shopDomain, token, variantGids, warehouseAxId) {
  const key = `${shopDomain}|${warehouseAxId || ''}|${[...variantGids].sort().join(',')}`;
  if (!variantsAvailabilityCache.has(key)) {
    const client = new StorefrontClient(shopDomain, token);
    variantsAvailabilityCache.set(
      key,
      client.fetchVariantsAvailability(variantGids, warehouseAxId).catch((err) => {
        variantsAvailabilityCache.delete(key);
        throw err;
      })
    );
  }
  return variantsAvailabilityCache.get(key);
}

const BOLIGHUSSIDE_METAFIELD = `
  metafield(namespace: "custom", key: "bolighusside") {
    reference {
      ... on Metaobject {
        fields {
          key
          value
          reference {
            ... on Page {
              handle
              onlineStoreUrl
            }
          }
          references(first: 20) {
            nodes {
              ... on Metaobject {
                fields { key value }
              }
            }
          }
        }
      }
    }
  }
`;

const LOCATION_FIELDS = `
  id
  name
  address { address1 address2 city province zip country countryCode phone latitude longitude }
  axLocationId: metafield(namespace: "ax_ns", key: "location_id") { value }
  ${BOLIGHUSSIDE_METAFIELD}
`;

const VARIANT_DISPLAY_FIELDS = `
  id
  displayedInStores: metafield(namespace: "ax_ns", key: "displayed_in_stores") { value }
`;

const PRODUCT_VARIANTS_DISPLAY_QUERY = `
  query ProductVariantsDisplay($productId: ID!, $cursor: String!) {
    product(id: $productId) {
      variants(first: 250, after: $cursor) {
        nodes { ${VARIANT_DISPLAY_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

// @inContext with no argument reads quantityAvailable in the shop's own primary market
// (DK on ilvadk, SE on ilvase) -- the figure the warehouse verdict needs (ClickUp 869fb4a09,
// see deliveryWarehouseInStock). Keyed on the shop, never the visitor, so no $country.
// centrallagerInStock is the warehouse's own yes/no (869fbkn3h round 2, same function); it
// reads null until the definition's Storefront access is Read. VARIANTS_AVAILABILITY_QUERY
// and card-purchasability.js's QUERY carry both too.
const VARIANT_AVAILABILITY_QUERY = `
  query VariantAvailability($variantId: ID!) @inContext {
    node(id: $variantId) {
      ... on ProductVariant {
        id
        displayedInStores: metafield(namespace: "ax_ns", key: "displayed_in_stores") { value }
        centrallagerInStock: metafield(namespace: "dtails", key: "centrallager_in_stock") { value }
        product {
          id
          variants(first: 250) {
            nodes { ${VARIANT_DISPLAY_FIELDS} }
            pageInfo { hasNextPage endCursor }
          }
        }
        quantityAvailable
        storeAvailability(first: 100) {
          nodes {
            available
            quantityAvailable
            pickUpTime
            location { id }
          }
        }
      }
    }
    locations(first: 100) {
      nodes { ${LOCATION_FIELDS} }
    }
  }
`;

// first: 100 on both connections for the reason VARIANT_AVAILABILITY_QUERY has it:
// the warehouse verdict compares against the best-stocked shop, so every pickup row
// has to arrive (see deliveryWarehouseInStock). @inContext and centrallagerInStock for
// the same reasons as there (869fb4a09, 869fbkn3h): the flag first, the market-context
// comparison when it is missing or says no.
const VARIANTS_AVAILABILITY_QUERY = `
  query VariantsAvailability($ids: [ID!]!) @inContext {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        centrallagerInStock: metafield(namespace: "dtails", key: "centrallager_in_stock") { value }
        quantityAvailable
        storeAvailability(first: 100) {
          nodes {
            quantityAvailable
            location { id }
          }
        }
      }
    }
    locations(first: 100) {
      nodes {
        id
        axLocationId: metafield(namespace: "ax_ns", key: "location_id") { value }
      }
    }
  }
`;

const LOCATIONS_QUERY = `
  query Locations {
    locations(first: 50) {
      nodes { ${LOCATION_FIELDS} }
    }
  }
`;

const LOCATIONS_NEAR_QUERY = `
  query LocationsNear($near: GeoCoordinateInput!) {
    locations(first: 50, sortKey: DISTANCE, near: $near) {
      nodes { ${LOCATION_FIELDS} }
    }
  }
`;

function readComponentTranslations(el) {
  const script = el.querySelector('script[data-translations-json]');
  try {
    return JSON.parse(script?.textContent || '{}');
  } catch (e) {
    return {};
  }
}

function parseListValue(value) {
  if (!value) return [];
  if (value.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch (e) {
      // fall through to treating the value as a single entry
    }
  }
  return [value];
}

function normalizeBolighusFields(metafield) {
  const result = {};
  const fields = metafield?.reference?.fields;
  if (!fields) return result;
  for (const f of fields) {
    if (f.key === 'opening_hours' && f.references?.nodes) {
      result.openingHours = f.references.nodes.map((obj) => {
        const entry = {};
        for (const field of obj.fields) entry[field.key] = field.value;
        return {
          title: entry.title || '',
          openTime: entry.open_time || '',
          closeTime: entry.close_time || '',
          closed: entry.closed === 'true',
          dates: entry.dates ? JSON.parse(entry.dates) : [],
          applicableDays: entry.applicable_days ? JSON.parse(entry.applicable_days) : [],
        };
      });
    } else if (f.key === 'store_page') {
      result.pageUrl = f.reference?.onlineStoreUrl || '';
    } else if (f.key === 'outlet_section') {
      result.isOutlet = f.value === 'true';
    } else if (f.key === 'product_pickup_in_store') {
      result.pickupInStore = f.value === 'true';
    } else if (f.key === 'product_pickup_address') {
      result.pickupAddress = f.value || '';
    } else if (f.key === 'bolighus_information') {
      result.bolighusInfo = parseListValue(f.value);
    }
  }
  return result;
}

function parseDisplayedInStores(value) {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

// Set of AX location ids where at least one sibling variant of the chosen
// variant is on display, so the drawer can say "displayed in another variant".
function collectOtherVariantsDisplayedInStores(variantId, siblingVariants) {
  const result = new Set();
  for (const sibling of siblingVariants) {
    if (sibling.id === variantId) continue;
    const displayed = parseDisplayedInStores(sibling.displayedInStores?.value);
    for (const [axLocationId, isDisplayed] of Object.entries(displayed)) {
      if (isDisplayed === true) result.add(axLocationId);
    }
  }
  return result;
}

function normalizeLocationNode(loc) {
  const meta = normalizeBolighusFields(loc.metafield);
  return {
    id: loc.id,
    name: loc.name,
    address: loc.address,
    lat: loc.address?.latitude,
    lng: loc.address?.longitude,
    openingHours: meta.openingHours || [],
    pageUrl: meta.pageUrl || '',
    isOutlet: meta.isOutlet || false,
    pickupInStore: meta.pickupInStore || false,
    pickupAddress: meta.pickupAddress || '',
    bolighusInfo: meta.bolighusInfo || [],
    axLocationId: loc.axLocationId?.value || '',
  };
}

/* --- Opening Hours Calculator --- */

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  return m === '00' ? `${parseInt(h)}` : `${parseInt(h)}:${m}`;
}

function findEntryForDate(openingHours, date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd}`;
  const dayName = DAY_NAMES[date.getDay()];

  let entry = openingHours.find((e) => e.dates?.length && e.dates.includes(dateStr));
  if (!entry) {
    entry = openingHours.find((e) =>
      e.applicableDays?.length && e.applicableDays.map((d) => d.toLowerCase()).includes(dayName)
    );
  }
  return entry || null;
}

function findNextOpenEntry(openingHours, translations) {
  const now = new Date();
  for (let offset = 1; offset <= 7; offset++) {
    const future = new Date(now);
    future.setDate(future.getDate() + offset);
    const entry = findEntryForDate(openingHours, future);
    if (entry && !entry.closed) {
      const futureDay = DAY_NAMES[future.getDay()];
      const dayLabel = offset === 1 ? (translations.tomorrow || '') : (translations.days?.[futureDay] || futureDay);
      return { entry, dayLabel };
    }
  }
  return null;
}

function getStoreStatus(openingHours, translations = {}) {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const entry = findEntryForDate(openingHours, now);

  const buildClosed = () => {
    const next = findNextOpenEntry(openingHours, translations);
    let detail = '';
    if (next) {
      detail = (translations.opens_at_day || '')
        .replace('{{ day }}', next.dayLabel)
        .replace('{{ time }}', formatTime(next.entry.openTime));
    }
    return { status: 'closed', detail };
  };

  if (!entry || entry.closed) return buildClosed();

  const openMinutes = parseTime(entry.openTime);
  const closeMinutes = parseTime(entry.closeTime);
  if (openMinutes === null || closeMinutes === null) return buildClosed();

  const SOON_THRESHOLD = 30;

  if (currentMinutes < openMinutes) {
    if (openMinutes - currentMinutes <= SOON_THRESHOLD) {
      const detail = (translations.opens_at || '').replace('{{ time }}', formatTime(entry.openTime));
      return { status: 'opening_soon', detail };
    }
    const detail = (translations.opens_at || '').replace('{{ time }}', formatTime(entry.openTime));
    return { status: 'closed', detail };
  }

  if (currentMinutes >= closeMinutes) return buildClosed();

  const closesDetail = (translations.closes_at || '').replace('{{ time }}', formatTime(entry.closeTime));

  if (closeMinutes - currentMinutes <= SOON_THRESHOLD) {
    return { status: 'closing_soon', detail: closesDetail };
  }

  return { status: 'open', detail: closesDetail };
}

function parseTime(str) {
  if (!str) return null;
  const parts = str.split(':');
  if (parts.length < 2) return null;
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

/* --- Stock Badge Calculator --- */

function getStockBadge(quantityAvailable, lowThreshold, showQuantity, translations) {
  if (quantityAvailable <= 0) {
    return { level: 'out-of-stock', text: translations.out_of_stock || '' };
  }

  if (lowThreshold > 0 && quantityAvailable <= lowThreshold) {
    const key = quantityAvailable === 1 ? 'one' : 'other';
    const template = translations.low_stock?.[key] || translations.low_stock || '';
    return {
      level: 'low-stock',
      text: template.replace('__count__', quantityAvailable),
    };
  }

  if (showQuantity) {
    const key = quantityAvailable === 1 ? 'one' : 'other';
    const template = translations.in_stock_with_count?.[key] || translations.in_stock || '';
    return {
      level: 'in-stock',
      text: template.replace('__count__', quantityAvailable),
    };
  }

  return { level: 'in-stock', text: translations.in_stock || '' };
}

/* --- Status Icons (inline SVG) --- */

const STATUS_ICONS = {
  stock: '<svg class="store-locator__icon store-locator__icon--positive" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.8334 9.4917H15.0417V14.9084H4.95841V9.4917H4.16675V15.7H15.8334V9.4917Z" fill="currentColor"/><path d="M16.5458 4.30005H3.4541V8.49172H16.5458V4.30005ZM15.7541 7.69588H4.24577V5.08755H15.7541V7.69588Z" fill="currentColor"/><path d="M11.3125 10.2417H8.6875V11.0334H11.3125V10.2417Z" fill="currentColor"/></svg>',
  stockWarning: '<svg class="store-locator__icon store-locator__icon--warning" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.8334 9.4917H15.0417V14.9084H4.95841V9.4917H4.16675V15.7H15.8334V9.4917Z" fill="currentColor"/><path d="M16.5458 4.30005H3.4541V8.49172H16.5458V4.30005ZM15.7541 7.69588H4.24577V5.08755H15.7541V7.69588Z" fill="currentColor"/><path d="M11.3125 10.2417H8.6875V11.0334H11.3125V10.2417Z" fill="currentColor"/></svg>',
  stockNegative: '<svg class="store-locator__icon store-locator__icon--negative" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.8334 9.4917H15.0417V14.9084H4.95841V9.4917H4.16675V15.7H15.8334V9.4917Z" fill="currentColor"/><path d="M16.5458 4.30005H3.4541V8.49172H16.5458V4.30005ZM15.7541 7.69588H4.24577V5.08755H15.7541V7.69588Z" fill="currentColor"/><path d="M11.3125 10.2417H8.6875V11.0334H11.3125V10.2417Z" fill="currentColor"/></svg>',
  displayPositive: '<svg class="store-locator__icon store-locator__icon--positive" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10.0001 5.10425C6.72928 5.10425 3.76261 6.90841 2.25428 9.81675L2.15845 10.0001L2.25428 10.1834C3.76261 13.0917 6.72928 14.8959 10.0001 14.8959C13.2709 14.8959 16.2376 13.0917 17.7459 10.1834L17.8418 10.0001L17.7459 9.81675C16.2376 6.90841 13.2709 5.10425 10.0001 5.10425ZM10.0001 14.1042C7.09595 14.1042 4.45428 12.5376 3.05428 10.0001C4.45428 7.46258 7.09595 5.89591 10.0001 5.89591C12.9043 5.89591 15.5459 7.46258 16.9459 10.0001C15.5459 12.5376 12.9043 14.1042 10.0001 14.1042Z" fill="currentColor"/><path d="M10.0001 7.40845C8.57095 7.40845 7.40845 8.57095 7.40845 10.0001C7.40845 11.4293 8.57095 12.5918 10.0001 12.5918C11.4293 12.5918 12.5918 11.4293 12.5918 10.0001C12.5918 8.57095 11.4293 7.40845 10.0001 7.40845ZM10.0001 11.8001C9.00845 11.8001 8.20011 10.9918 8.20011 10.0001C8.20011 9.00845 9.00845 8.20011 10.0001 8.20011C10.9918 8.20011 11.8001 9.00845 11.8001 10.0001C11.8001 10.9918 10.9918 11.8001 10.0001 11.8001Z" fill="currentColor"/></svg>',
  displayNegative: '<svg class="store-locator__icon store-locator__icon--negative" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M9.99998 5.10426C9.44998 5.10426 8.90832 5.15843 8.37915 5.25843L8.87915 5.9751C9.24582 5.9251 9.62082 5.89593 9.99582 5.89593C12.9 5.89593 15.5417 7.4626 16.9417 10.0001C16.2083 11.3251 15.1333 12.3834 13.8625 13.0959L14.3167 13.7459C15.75 12.9293 16.9458 11.7084 17.7375 10.1793L17.8333 9.99593L17.7375 9.8126C16.2292 6.90426 13.2625 5.1001 9.99165 5.1001L9.99998 5.10426Z" fill="currentColor"/><path d="M12.6543 14.4793L15.0084 17.8459L15.6584 17.3918L15.0793 16.5626L5.97926 3.57095L4.99176 2.15845L4.34176 2.61261L6.57926 5.80428C4.7501 6.58345 3.2126 7.97928 2.25843 9.81678L2.1626 10.0001L2.25843 10.1834C3.76676 13.0918 6.73343 14.8959 10.0043 14.8959C10.9209 14.8959 11.8126 14.7501 12.6584 14.4793H12.6543ZM10.6834 11.6668C10.4709 11.7543 10.2418 11.8043 10.0001 11.8043C9.00843 11.8043 8.2001 10.9959 8.2001 10.0043C8.2001 9.53761 8.38343 9.11678 8.6751 8.79595L10.6876 11.6668H10.6834ZM3.05426 10.0001C3.95426 8.36678 5.3751 7.13761 7.04176 6.47095L8.20426 8.13345C7.7126 8.60428 7.40426 9.26678 7.40426 10.0043C7.40426 11.4334 8.56676 12.5959 9.99593 12.5959C10.4084 12.5959 10.7959 12.4959 11.1418 12.3251L12.1751 13.8001C11.4793 14.0001 10.7459 14.1043 10.0001 14.1043C7.09593 14.1043 4.45426 12.5376 3.05426 10.0001Z" fill="currentColor"/><path d="M12.3958 10.9959C12.5249 10.6876 12.5958 10.3543 12.5958 10.0001C12.5958 8.57095 11.4333 7.40845 10.0041 7.40845C9.9666 7.40845 9.9291 7.40845 9.8916 7.41261L10.4958 8.27511C11.2499 8.49178 11.8083 9.17928 11.8083 10.0001C11.8083 10.0459 11.7999 10.0876 11.7958 10.1293L12.3999 10.9959H12.3958Z" fill="currentColor"/></svg>',
};

/* --- Map Provider Adapters --- */

class MapboxAdapter {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.map = null;
    this.markers = [];
    this.loaded = false;
  }

  async load() {
    if (this.loaded) return;
    if (!document.querySelector('link[href*="mapbox-gl"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css';
      document.head.appendChild(link);
    }
    if (!window.mapboxgl) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    window.mapboxgl.accessToken = this.apiKey;
    this.loaded = true;
  }

  async initMap(container, options = {}) {
    await this.load();
    this.map = new window.mapboxgl.Map({
      container,
      style: 'mapbox://styles/webmasterilva/cmtigrql800ao01qtd84y571k',
      center: [options.lng || 10.2, options.lat || 56.15],
      zoom: options.zoom || 7,
      attributionControl: false,
    });
    this.map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
    this.map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    return this.map;
  }

  addMarker(lat, lng, data, onClick, options = {}) {
    if (!this.map) return null;
    const markerOpts = {};
    if (options.color) markerOpts.color = options.color;
    const marker = new window.mapboxgl.Marker(markerOpts)
      .setLngLat([lng, lat])
      .addTo(this.map);
    if (onClick) marker.getElement().addEventListener('click', () => onClick(data));
    this.markers.push(marker);
    return marker;
  }

  clearMarkers() {
    this.markers.forEach((m) => m.remove());
    this.markers = [];
  }

  fitBounds(locations) {
    if (!this.map || locations.length === 0) return;
    if (locations.length === 1) {
      this.map.flyTo({ center: [locations[0].lng, locations[0].lat], zoom: 14 });
      return;
    }
    const bounds = new window.mapboxgl.LngLatBounds();
    for (const loc of locations) bounds.extend([loc.lng, loc.lat]);
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 14 });
  }

  smartZoom(center, closestStores, { minZoom = 10, maxZoom = 15 } = {}) {
    if (!this.map) return;
    if (closestStores.length === 0) {
      this.map.flyTo({ center: [center.lng, center.lat], zoom: 14 });
      return;
    }

    const closest = closestStores[0];
    const closestLatD = Math.abs(closest.lat - center.lat);
    const closestLngD = Math.abs(closest.lng - center.lng);

    let maxLatD = closestLatD;
    let maxLngD = closestLngD;
    for (const s of closestStores) {
      maxLatD = Math.max(maxLatD, Math.abs(s.lat - center.lat));
      maxLngD = Math.max(maxLngD, Math.abs(s.lng - center.lng));
    }

    const boundsForN = new window.mapboxgl.LngLatBounds(
      [center.lng - maxLngD, center.lat - maxLatD],
      [center.lng + maxLngD, center.lat + maxLatD]
    );
    const boundsFor1 = new window.mapboxgl.LngLatBounds(
      [center.lng - closestLngD, center.lat - closestLatD],
      [center.lng + closestLngD, center.lat + closestLatD]
    );

    const zoomForN = this.map.cameraForBounds(boundsForN, { padding: 50 })?.zoom || minZoom;
    const zoomFor1 = this.map.cameraForBounds(boundsFor1, { padding: 50 })?.zoom || minZoom;

    const zoom = Math.min(Math.max(zoomForN, minZoom), zoomFor1, maxZoom);
    this.map.flyTo({ center: [center.lng, center.lat], zoom });
  }

  async geocode(query) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=dk&language=da&access_token=${this.apiKey}&limit=1`;
    const res = await fetch(url);
    const json = await res.json();
    if (!json.features?.length) return null;
    const [lng, lat] = json.features[0].center;
    return { lat, lng };
  }

  async suggest(query, limit = 5) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=dk&language=da&access_token=${this.apiKey}&limit=${limit}&autocomplete=true&types=place,postcode,address,locality`;
    const res = await fetch(url);
    const json = await res.json();
    if (!json.features?.length) return [];
    return json.features.map((f) => ({
      text: f.place_name,
      lat: f.center[1],
      lng: f.center[0],
    }));
  }
}

class GoogleMapAdapter {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.map = null;
    this.markers = [];
    this.loaded = false;
  }

  async load() {
    if (this.loaded) return;
    if (!window.google?.maps) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    this.loaded = true;
  }

  async initMap(container, options = {}) {
    await this.load();
    this.map = new window.google.maps.Map(container, {
      center: { lat: options.lat || 56.15, lng: options.lng || 10.2 },
      zoom: options.zoom || 7,
    });
    return this.map;
  }

  addMarker(lat, lng, data, onClick, options = {}) {
    if (!this.map) return null;
    const markerOpts = { position: { lat, lng }, map: this.map };
    if (options.color) {
      const svg = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${options.color}"/><circle cx="12" cy="12" r="4" fill="#fff"/></svg>`)}`;
      markerOpts.icon = { url: svg, scaledSize: new window.google.maps.Size(24, 36) };
    }
    const marker = new window.google.maps.Marker(markerOpts);
    if (onClick) marker.addListener('click', () => onClick(data));
    this.markers.push(marker);
    return marker;
  }

  clearMarkers() {
    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];
  }

  fitBounds(locations) {
    if (!this.map || locations.length === 0) return;
    if (locations.length === 1) {
      this.map.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
      this.map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    for (const loc of locations) bounds.extend({ lat: loc.lat, lng: loc.lng });
    this.map.fitBounds(bounds);
  }

  smartZoom(center, closestStores, { minZoom = 10, maxZoom = 15 } = {}) {
    if (!this.map) return;
    if (closestStores.length === 0) {
      this.map.setCenter({ lat: center.lat, lng: center.lng });
      this.map.setZoom(14);
      return;
    }

    const closest = closestStores[0];
    const closestLatD = Math.abs(closest.lat - center.lat);
    const closestLngD = Math.abs(closest.lng - center.lng);

    let maxLatD = closestLatD;
    let maxLngD = closestLngD;
    for (const s of closestStores) {
      maxLatD = Math.max(maxLatD, Math.abs(s.lat - center.lat));
      maxLngD = Math.max(maxLngD, Math.abs(s.lng - center.lng));
    }

    const boundsForN = new window.google.maps.LatLngBounds(
      { lat: center.lat - maxLatD, lng: center.lng - maxLngD },
      { lat: center.lat + maxLatD, lng: center.lng + maxLngD }
    );
    this.map.fitBounds(boundsForN);
    window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
      const zoomForN = this.map.getZoom();
      const zoom = Math.min(Math.max(zoomForN, minZoom), maxZoom);
      this.map.setCenter({ lat: center.lat, lng: center.lng });
      this.map.setZoom(zoom);
    });
  }

  async geocode(query) {
    await this.load();
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address: query, componentRestrictions: { country: 'dk' } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
          } else {
            resolve(null);
          }
        }
      );
    });
  }

  async suggest() {
    return [];
  }
}

function createMapAdapter(provider, apiKey) {
  if (provider === 'google') return new GoogleMapAdapter(apiKey);
  return new MapboxAdapter(apiKey);
}

/* --- Utility: distance between two coords (km) --- */

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* --- Utility: debounce --- */

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/* --- Utility: browser geolocation --- */

function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10000 }
    );
  });
}

/* --- Utility: render helpers --- */

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function buildDirectionsUrl(address) {
  const query = [address?.address1, address?.city, address?.zip, address?.country].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}` : '';
}

/* Maps opening_hours metaobject entries (already authored as condensed slots,
   e.g. title "Man \u2013 Fre") to display rows. Date-only entries are holiday
   overrides; consumers showing only the regular schedule can exclude them. */
function hoursRows(openingHours, translations = {}, { includeDateEntries = true } = {}) {
  const todayDay = DAY_NAMES[new Date().getDay()];
  return (openingHours || [])
    .filter((e) => e.applicableDays?.length || (includeDateEntries && e.dates?.length))
    .map((e) => ({
      label: e.title || '',
      time: e.closed ? translations.closed || '' : `${e.openTime} \u2013 ${e.closeTime}`,
      isToday: e.applicableDays?.map((d) => d.toLowerCase()).includes(todayDay) || false,
    }));
}

function renderHoursList(openingHours, translations = {}) {
  if (!openingHours?.length) return '';
  const rows = hoursRows(openingHours, translations)
    .map((row) => {
      const activeClass = row.isToday ? ' is-today' : '';
      return `<div class="store-locator__hours-row text-body-small${activeClass}"><span class="store-locator__hours-day">${row.label}</span><span class="store-locator__hours-time">${row.time}</span></div>`;
    })
    .join('');
  return `<div class="store-locator__hours-list">${rows}</div>`;
}

function renderAddress(address) {
  if (!address) return '';
  const parts = [address.address1, address.address2, `${address.zip || ''} ${address.city || ''}`.trim()].filter(Boolean);
  return parts.join('<br>');
}

/* --- GA4 dataLayer helpers (s360 spec, ClickUp 869ejnku8) ---
   The availability fields only exist in the PDP drawer context - the
   standalone store locator page has no variant, so they fall back to empty
   strings per the spec's "not available -> empty string" rule.

   The directions link reports a per-surface event name (s360, 2026-09-23):
   route_directions_pdp from the drawer, route_directions_find_store_page from
   the Find bolighus page. Each caller passes its name through
   renderPopupCard's options; the name follows the component, not the URL, so
   a Featured product's drawer on an article still reports _pdp. */
function dlStoreParams(store) {
  return {
    store: store.name || '',
    in_stock: store.quantityAvailable === undefined ? '' : (store.quantityAvailable > 0 ? 'yes' : 'no'),
    on_display: store.onDisplay === undefined ? '' : (store.onDisplay ? 'yes' : 'no'),
  };
}

function dlLinkAttrs(event, store) {
  const params = dlStoreParams(store);
  const esc = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  return ` data-dl-event="${event}" data-dl-store="${esc(params.store)}" data-dl-in-stock="${params.in_stock}" data-dl-on-display="${params.on_display}"`;
}

function renderPopupCard(store, translations, options = {}) {
  const storeStatus = getStoreStatus(store.openingHours, translations);
  const POPUP_STATUS_MAP = { open: 'status_open', closing_soon: 'closing_soon', opening_soon: 'opening_soon', closed: 'status_closed' };
  const popupStatusWord = translations[POPUP_STATUS_MAP[storeStatus.status]] || '';
  const popupDetailHtml = storeStatus.detail ? ` – ${storeStatus.detail}` : '';
  const statusHtml = `<div class="store-locator__popup-status text-sm"><span class="store-locator__status-text store-locator__status-text--${storeStatus.status}">${popupStatusWord}</span>${popupDetailHtml}</div>`;
  const hoursList = renderHoursList(store.openingHours, translations);
  const address = renderAddress(store.address);
  const phone = store.address?.phone || '';

  let stockHtml = '';
  if (options.stockBadge) {
    stockHtml = `<div class="store-locator__badge text-sm store-locator__badge--${options.stockBadge.level}">${options.stockBadge.text}</div>`;
  }

  let pickupHtml = '';
  if (options.pickUpTime) {
    pickupHtml = `<div class="text-sm">${options.pickUpTime}</div>`;
  }

  const hoursLabel = translations.hours_label || '';

  const readMoreText = store.pageUrl && translations.read_more_store
    ? (translations.read_more_store || '').replace('{{ name }}', store.name)
    : '';
  const readMoreLink = readMoreText
    ? `<a href="${store.pageUrl}" class="store-locator__popup-link text-sm"${dlLinkAttrs('store_information', store)}>${readMoreText}</a>`
    : '';

  const directionsLink = `<a href="${buildDirectionsUrl(store.address)}" target="_blank" rel="noopener" class="store-locator__popup-link text-sm"${dlLinkAttrs(options.directionsEvent || 'route_directions', store)}>${translations.get_directions || ''}</a>`;

  return `
    <div class="store-locator__popup">
      <button type="button" class="store-locator__popup-close" data-close-popup aria-label="${translations.close || ''}">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5"/></svg>
      </button>
      <div class="store-locator__popup-name text-body-large">${store.name}</div>
      ${stockHtml || pickupHtml ? `<div class="store-locator__popup-stock">${stockHtml}${pickupHtml}</div>` : ''}
      <div class="store-locator__popup-address-section">
        ${address ? `<address class="store-locator__popup-address text-body-small">${address}</address>` : ''}
        ${phone ? `<div class="store-locator__popup-phone text-body-small">${phone}</div>` : ''}
      </div>
      ${hoursList ? `<div class="store-locator__popup-hours-section">${statusHtml}<div class="store-locator__popup-label text-xs">${hoursLabel}</div>${hoursList}</div>` : ''}
      <div class="store-locator__popup-links">
        ${readMoreLink}
        ${directionsLink}
      </div>
    </div>
  `;
}

/* --- Search + Suggestions Mixin --- */

const SearchMixin = {
  initSearch() {
    this.searchBtn?.addEventListener('click', () => this.handleSearch());
    this.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSearch();
    });
    this._debouncedLiveSearch = debounce(() => this._liveSearch(), 300);
    this.searchInput?.addEventListener('input', () => { this._usingGeolocation = false; this.hideLocationError?.(); this.toggleClearButton(); this._debouncedLiveSearch(); });
    this.searchClear?.addEventListener('click', () => this.clearSearch());
  },

  async _liveSearch() {
    const query = this.searchInput?.value?.trim();
    if (!query || query.length < 2) {
      if (!query) this.resetToDefault();
      return;
    }
    if (!this.config.mapApiKey || this._usingGeolocation) return;
    if (!this._searchAdapter) this._searchAdapter = createMapAdapter(this.config.mapProvider, this.config.mapApiKey);
    const results = await this._searchAdapter.suggest(query, 1);
    if (!results.length || this.searchInput?.value?.trim() !== query) return;
    this.applySearchCoords({ lat: results[0].lat, lng: results[0].lng });
    this._trackStoreSearch(query);
  },

  /* GA4: one store_search per resolved query - the live search fires per
     debounce, so consecutive identical terms are deduped. */
  _trackStoreSearch(query) {
    if (!query || query === this._dlLastSearch) return;
    this._dlLastSearch = query;
    window.ilvaTrack?.('store_search', { search_term: query });
  },

  resetToDefault() {
    for (const store of this.stores) delete store._distance;
    this.stores.sort((a, b) => a.name.localeCompare(b.name, 'da'));
    this.resetComponentState();
    this.listEl?.scrollTo(0, 0);
    this._dlLastSearch = null;
  },

  toggleClearButton() {
    if (!this.searchClear) return;
    this.searchClear.hidden = !this.searchInput?.value?.trim();
  },

  sortByDistance(coords) {
    for (const store of this.stores) {
      if (store.lat && store.lng) {
        store._distance = haversineKm(coords.lat, coords.lng, store.lat, store.lng);
      }
    }
    this.stores.sort((a, b) => (a._distance ?? 9999) - (b._distance ?? 9999));
    this.listEl?.scrollTo(0, 0);
  },

  async handleSearch() {
    const query = this.searchInput?.value?.trim();
    this.hideLocationError?.();
    if (!query || !this.config.mapApiKey || this._usingGeolocation) return;
    if (!this._searchAdapter) this._searchAdapter = createMapAdapter(this.config.mapProvider, this.config.mapApiKey);
    const coords = await this._searchAdapter.geocode(query);
    if (!coords) {
      this.onSearchNoResults?.(query);
      return;
    }
    this.applySearchCoords(coords);
    this._trackStoreSearch(query);
    this.toggleClearButton();
    this.updateUrlParam?.(query);
  },

  _getButtonTextNode(btn) {
    return [...btn.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
  },

  async handleUseMyLocation() {
    if (!this.useLocationBtn) return;
    const textNode = this._getButtonTextNode(this.useLocationBtn);
    const originalText = textNode?.textContent;
    this.hideLocationError?.();
    this.useLocationBtn.disabled = true;
    if (textNode) textNode.textContent = this.translations.locating || '';

    try {
      const coords = await getBrowserLocation();
      this._usingGeolocation = true;
      if (this.searchInput) this.searchInput.value = this.translations.use_my_location || '';
      this.toggleClearButton();
      this.updateUrlParam?.(null);
      this.applySearchCoords(coords);
    } catch {
      /* The page reports this under the button that triggered it; the drawer has
         no dedicated slot, so it keeps using the shared error row. */
      if (this.showLocationError) {
        this.showLocationError(this.translations.location_error);
      } else {
        this.showError(this.translations.location_error);
      }
    } finally {
      this.useLocationBtn.disabled = false;
      if (textNode) textNode.textContent = originalText;
    }
  },

  clearSearch() {
    this._usingGeolocation = false;
    this.hideLocationError?.();
    if (this.searchInput) this.searchInput.value = '';
    this.toggleClearButton();
    this.updateUrlParam?.(null);
    this._dlLastSearch = null;
    this.resetToDefault();
  },
};

/* --- StoreLocatorDrawer Web Component (PDP) --- */

class StoreLocatorDrawer extends HTMLElement {
  connectedCallback() {
    this.config = {
      shopDomain: this.dataset.shopDomain,
      token: this.dataset.storefrontToken,
      variantGid: this.dataset.variantGid,
      mapApiKey: this.dataset.mapApiKey,
      mapProvider: this.dataset.mapProvider || 'mapbox',
      lowThreshold: parseInt(this.dataset.lowThreshold, 10) || 5,
      showQuantity: this.dataset.showQuantity !== 'false',
    };

    this.translations = readComponentTranslations(this);

    this.stores = [];
    this.filteredStores = [];
    this.searchCoords = null;
    this.loaded = false;

    this.listEl = this.querySelector('[data-store-list]');
    this.loadingEl = this.querySelector('[data-store-loading]');
    this.emptyEl = this.querySelector('[data-store-empty]');
    this.errorEl = this.querySelector('[data-store-error]');
    this.popupOverlay = document.querySelector(`[data-store-locator-popup="${this.dataset.popupId}"]`);
    this.searchInput = this.querySelector('[data-store-search-input]');
    this.searchBtn = this.querySelector('[data-store-search-btn]');
    this.searchClear = this.querySelector('[data-store-search-clear]');
    this.filterInStock = this.querySelector('[data-filter-in-stock]');
    this.filterOnDisplay = this.querySelector('[data-filter-on-display]');

    this.initSearch();
    this.useLocationBtn = this.querySelector('[data-use-my-location]');
    this.useLocationBtn?.addEventListener('click', () => this.handleUseMyLocation());

    this.filterInStock?.addEventListener('change', () => this.applyFilters());
    this.filterOnDisplay?.addEventListener('change', () => this.applyFilters());

    const sideDrawer = this.closest('side-drawer');
    if (sideDrawer) {
      if (sideDrawer.hasAttribute('open')) {
        this.loaded = true;
        this.loadData();
      } else {
        this.observer = new MutationObserver(() => {
          if (sideDrawer.hasAttribute('open') && !this.loaded) {
            this.loaded = true;
            this.observer.disconnect();
            this.loadData();
          }
        });
        this.observer.observe(sideDrawer, { attributes: true, attributeFilter: ['open'] });
      }
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async loadData() {
    if (!this.config.token || !this.config.shopDomain) {
      this.showError(this.translations.error_loading);
      return;
    }

    this.showLoading();
    try {
      const { stores } = await fetchVariantAvailabilityCached(
        this.config.shopDomain,
        this.config.token,
        this.config.variantGid
      );
      this.stores = stores;
      this.applyFilters();
    } catch (err) {
      console.error('Store locator load error:', err);
      this.showError(this.translations.error_loading);
    }
  }

  resolveDisplayBadge(store) {
    if (store.onDisplay) {
      return {
        icon: STATUS_ICONS.displayPositive,
        text: this.translations.on_display_in_store || '',
        className: 'store-locator__badge--in-stock',
      };
    }
    if (store.onDisplayOtherVariant) {
      return {
        icon: STATUS_ICONS.displayPositive,
        text: this.translations.on_display_other_variant || '',
        className: 'store-locator__badge--in-stock',
      };
    }
    return {
      icon: STATUS_ICONS.displayNegative,
      text: this.translations.not_on_display || '',
      className: 'store-locator__badge--out-of-stock',
    };
  }

  applyFilters() {
    let stores = [...this.stores];
    if (this.filterInStock?.checked) {
      stores = stores.filter((s) => s.quantityAvailable > 0);
    }
    if (this.filterOnDisplay?.checked) {
      stores = stores.filter((s) => s.onDisplay || s.onDisplayOtherVariant);
    }
    this.filteredStores = stores;
    this.renderList();
  }

  renderList() {
    if (!this.listEl) return;
    if (this.filteredStores.length === 0) {
      this.listEl.innerHTML = '';
      this.showEmpty();
      return;
    }
    this.hideStates();
    this.listEl.innerHTML = this.filteredStores
      .map((store) => {
        const badge = getStockBadge(
          store.quantityAvailable,
          this.config.lowThreshold,
          this.config.showQuantity,
          this.translations
        );

        const stockIcon = badge.level === 'in-stock' ? STATUS_ICONS.stock
          : badge.level === 'low-stock' ? STATUS_ICONS.stockWarning
          : STATUS_ICONS.stockNegative;

        const display = this.resolveDisplayBadge(store);

        return `
          <li data-store-id="${store.id}">
            <div class="store-locator__card">
              <div class="store-locator__card-left">
                <div class="store-locator__store-name">${store.name}</div>
                <button type="button" class="store-locator__details-link text-sm" data-show-store-popup>
                  ${this.translations.address_and_hours || ''}
                </button>
              </div>
              <div class="store-locator__card-right">
                <span class="store-locator__badge text-sm ${display.className}">${display.icon} ${display.text}</span>
                <span class="store-locator__badge text-sm store-locator__badge--${badge.level}">${stockIcon} ${badge.text}</span>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    this.listEl.querySelectorAll('[data-show-store-popup]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const li = e.target.closest('li');
        const storeId = li?.dataset.storeId;
        const store = this.filteredStores.find((s) => s.id === storeId);
        if (store) this.openPopup(store);
      });
    });
  }

  openPopup(store) {
    if (!this.popupOverlay) return;
    // GA4: the popup IS the address + opening hours view (s360 spec).
    window.ilvaTrack?.('address_and_opening_hours', dlStoreParams(store));
    this.popupOverlay.innerHTML = renderPopupCard(store, this.translations, { directionsEvent: 'route_directions_pdp' });
    this.popupOverlay.classList.add('is-active');
    this.popupOverlay.querySelector('[data-close-popup]')?.addEventListener('click', () => this.closePopup());
    this.popupOverlay.addEventListener('click', (e) => {
      if (e.target === this.popupOverlay) this.closePopup();
    }, { once: true });
  }

  closePopup() {
    this.popupOverlay?.classList.remove('is-active');
  }

  applySearchCoords(coords) {
    this.searchCoords = coords;
    this.sortByDistance(coords);
    this.applyFilters();
  }

  resetComponentState() {
    this.searchCoords = null;
    this.applyFilters();
  }

  showLoading() {
    this.hideStates();
    if (this.loadingEl) this.loadingEl.style.display = '';
  }

  showEmpty() {
    this.hideStates();
    if (this.emptyEl) this.emptyEl.style.display = '';
  }

  showError(msg) {
    this.hideStates();
    if (this.errorEl) {
      this.errorEl.textContent = msg || '';
      this.errorEl.style.display = '';
    }
  }

  hideStates() {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.emptyEl) this.emptyEl.style.display = 'none';
    if (this.errorEl) this.errorEl.style.display = 'none';
  }
}

Object.assign(StoreLocatorDrawer.prototype, SearchMixin);

if (!customElements.get('store-locator-drawer')) {
  customElements.define('store-locator-drawer', StoreLocatorDrawer);
}

/* --- StoreStockSummary Web Component (PDP delivery info card) --- */

class StoreStockSummary extends HTMLElement {
  async connectedCallback() {
    const { shopDomain, storefrontToken, variantGid, template } = this.dataset;
    const textEl = this.querySelector('[data-store-stock-text]');
    if (!shopDomain || !storefrontToken || !variantGid || !template || !textEl) return;

    try {
      const { stores, totalStores } = await fetchVariantAvailabilityCached(
        shopDomain,
        storefrontToken,
        variantGid
      );
      // The element is swapped out on variant change, so a late response may
      // belong to a variant that is no longer on screen.
      if (!this.isConnected || totalStores === 0) return;

      // quantityAvailable, not `available`: `available` counts stock that could be
      // transferred in, which would disagree with the drawer's stock badges.
      const inStock = stores.filter((store) => store.quantityAvailable > 0).length;
      textEl.textContent = template.replace('__count__', inStock).replace('__total__', totalStores);
      // GA4: the button carries data-dl-event="check_store_stock_status" (see
      // product-delivery-info.liquid); the count rides along as a param.
      textEl.dataset.dlStoresWithStock = String(inStock);
      this.hidden = false;
    } catch {
      // Stay hidden — the drawer surfaces its own error state when opened.
    }
  }
}

if (!customElements.get('store-stock-summary')) {
  customElements.define('store-stock-summary', StoreStockSummary);
}

/* --- DeliveryEstimate Web Component (PDP shipping + click & collect estimates) ---

   Implements the six-state delivery table (ClickUp 869ebk2gn): warehouse ("Vejle") /
   retail / factory stock decide which shipping and pick-up texts show. Client-side
   because the 13:00 cutoff and "tomorrow" math would break the page cache if done in
   Liquid, and per-location stock only exists in the Storefront API.

   Warehouse stock is deliveryWarehouseInStock's verdict -- the variant's Centrallager flag,
   with the market-context sellable figure against the best-stocked shop as the fallback; the
   warehouse (Centrallager Vejle) has local pickup disabled, so the Storefront API never lists
   it among the shops. Factory stock is an
   upcoming arrival date on the variant's ax_ns.incoming_info_key entry for the
   warehouse; a date in the past is treated as missing (stale feed). */

const DELIVERY_SHOP_TIMEZONE = 'Europe/Copenhagen';

/* All date math runs on UTC-midnight Date objects made from YYYY-MM-DD strings, so
   local timezones and DST can never shift a day. "Today" comes from the shop
   timezone, not the visitor's. */

function deliveryTodayInShopTime() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DELIVERY_SHOP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return {
    todayISO: `${get('year')}-${get('month')}-${get('day')}`,
    nowMinutes: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

function deliveryParseISODate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim())) return null;
  const date = new Date(`${String(value).trim()}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function deliveryParseCutoff(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return 13 * 60;
  return Number(match[1]) * 60 + Number(match[2]);
}

/* --- National holiday calendar (DK / SE) ---

   Computed, not configured. The dates are deterministic — fixed days plus
   Easter-relative ones — so nobody has to maintain a list that, left empty,
   silently produces delivery dates a day early. Which market applies follows the
   SHOP, not the visitor: goods ship from this store's warehouse on that country's
   working days, whoever is buying.

   De-facto closures (juleaftensdag, nytårsaftensdag, midsommarafton, grundlovsdag)
   are deliberately excluded — whether the warehouse ships those days is ILVA's
   call, not a calendar fact, so they belong in the "extra closure dates" setting. */

// Anonymous Gregorian computus. Returns Easter Sunday as a UTC-midnight Date.
function deliveryEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function deliveryNationalHolidays(year, country) {
  const iso = (date) => date.toISOString().slice(0, 10);
  const fixed = (month, day) => iso(new Date(Date.UTC(year, month - 1, day)));
  const easter = deliveryEasterSunday(year);
  const fromEaster = (offset) => iso(deliveryAddDays(easter, offset));

  // Days that always land on a Sunday (Easter Day, Whit Sunday) are omitted —
  // weekends are already skipped, so listing them would be noise.
  const shared = [
    fixed(1, 1), // New Year's Day
    fromEaster(-2), // Good Friday
    fromEaster(1), // Easter Monday
    fromEaster(39), // Ascension Day
    fixed(12, 25),
    fixed(12, 26),
  ];

  if (country === 'SE') {
    return shared.concat([
      fixed(1, 6), // Trettondedag jul
      fixed(5, 1), // Första maj
      fixed(6, 6), // Nationaldagen
    ]);
  }
  return shared.concat([
    fromEaster(-3), // Skærtorsdag
    fromEaster(50), // 2. pinsedag
  ]);
}

function deliveryParseLeadTime(value) {
  if (value === null || value === undefined || value === '') return null;
  const days = Number(value);
  return Number.isFinite(days) ? days : null;
}

function deliveryAddDays(date, days) {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function deliveryIsWorkday(date, holidays) {
  const weekday = date.getUTCDay();
  return weekday !== 0 && weekday !== 6 && !holidays.has(date.toISOString().slice(0, 10));
}

function deliveryAddWeekdays(date, count, holidays) {
  let result = new Date(date);
  let remaining = count;
  while (remaining > 0) {
    result = deliveryAddDays(result, 1);
    if (deliveryIsWorkday(result, holidays)) remaining--;
  }
  return result;
}

function deliveryRollToWorkday(date, holidays) {
  let result = new Date(date);
  while (!deliveryIsWorkday(result, holidays)) result = deliveryAddDays(result, 1);
  return result;
}

// D/M/YYYY — the year is part of every printed date on both stores (ClickUp 869fbkm2n).
function deliveryFormatDate(date) {
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}/${date.getUTCFullYear()}`;
}

/* Whether the central warehouse holds stock of a variant (ClickUp 869f86cbr, 869fb4a09,
   869fbkn3h round 2). The one definition every surface shares: the PDP delivery card, the
   purchasability gate and the configurator's short-delivery filter all read it from here.

   The flag first. Every variant carries "Centrallager in stock" (dtails.centrallager_in_stock),
   written by our app dtailsdk/ilva-product-linker as available > 0 at Centrallager Vejle,
   normally ~20 s after the level crosses zero. Every query feeding this rule reads it in the
   same Storefront response as the figures below (deliveryParseWarehouseFlag). It is null while
   the definition's Storefront access is off, and the rule is then exactly the comparison.

   The flag may only ADD "in stock". The app writes queued readings with no ordering check, so
   an out-of-order retry can leave it wrong until that variant's next Centrallager change or the
   app's 02:15 UTC reconciliation -- up to ~24 h. A stale false would hide a stocked warehouse,
   while the comparison is never wrong when it says "in stock" (only Centrallager sends store
   transfers), so false defers to the comparison. A stale true is the accepted risk: a live buy
   button on stock that has just gone, until the app catches up. A true also needs a sellable
   figure above 0: a shop oversold past the warehouse's count (aries-vitrineskab: warehouse 6,
   one shop -4) can take the figure to 0 or below, where Liquid already shows sold out and a
   delivery promise beside it would be wrong.

   The comparison, when the flag is missing or false. The storefront never exposes the
   warehouse's own count -- Vejle has no local pickup, so it is absent from storeAvailability --
   so it is derived from two fields with different scopes:
   - ProductVariant.quantityAvailable (largestSourceQuantity) is the online-sellable figure,
     read in the shop's primary market (bare @inContext, 869fb4a09). With warehouse stock it adds
     the warehouse to a shop figure (skovby-sm842: 4 + 2 = 6 against a best shop of 4); with an
     empty warehouse it equals the best shop; an oversold location pulls it down. With NO context
     it is only max(warehouse, best shop), so a warehouse tying its best shop read as empty.
     Shopify documents none of this -- it is measured.
   - StoreAvailability.quantityAvailable (storeQuantities) is physical on-hand stock on one
     shop floor. The context does not change it.
   So the warehouse is stocked when the sellable figure beats every shop. Its blind spot: the
   market figure adds the warehouse through a store transfer, and some bolighuse have none, so
   with one of them best-stocked a stocked warehouse reads as empty (9 of 2,928 in-stock
   variants on ilvadk, 0 of 2,641 on ilvase, 2026-10-03; rorbaek: warehouse 6, ILVA Skejby 20).
   The flag closes that; it is back only while the flag is missing. The comparison reads the
   whole pickup list, so a truncated storeAvailability page would drop the best shop and promise
   shipping on nothing -- keep every query's first: above the shop count (40 today). Empty
   storeQuantities (the variant id did not resolve) compares against 0. If Shopify changes how
   the market figure combines locations the fallback drifts silently: re-measure Admin's
   warehouse level against the Storefront figure with and without the context.

   Earlier rules, all wrong: subtracting summed shop stock (260cd43) read a stocked warehouse as
   empty; "> 0" (869f449m2) let one stocked shop pass for the warehouse. Liquid's
   variant.inventory_quantity is no shortcut: it renders the market figure too, and only as a
   page-load snapshot. */
function deliveryWarehouseInStock(largestSourceQuantity, storeQuantities, centrallagerInStock = null) {
  if (centrallagerInStock === true && largestSourceQuantity > 0) return true;
  const largestShop = storeQuantities.reduce((max, qty) => Math.max(max, qty), 0);
  return largestSourceQuantity > largestShop;
}

/* The Centrallager flag as the Storefront API returns it: { value: "true" | "false" } once the
   definition's Storefront access is Read, null while it is not. Strict string compare, never
   Boolean(value), which reads "false" as true. Anything else is null -- no verdict, and
   deliveryWarehouseInStock falls back to the comparison. card-purchasability.js keeps a copy
   (parseWarehouseFlag); qa/sold-out-online/parity.test.cjs holds the two equal. */
function deliveryParseWarehouseFlag(metafield) {
  const value = metafield?.value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

/* Pure resolver for the six-state table, plus the overselling rule that overrides it.
   Returns {shipping, pickup, warehouseInStock}; shipping/pickup are {key, date?} or null
   when that line can't be resolved.

   One deliberate departure from the client's table (ClickUp 869fbkn3h): its row 6 -- stock
   only in the shops, no factory arrival -- read "Udgået", which made a continue-selling
   variant unbuyable online for as long as one unit stood on a shop floor. The client's rule
   since 869f86cbr is that overselling decides "Udsolgt online", so rows 4/6 now ship on the
   same terms as rows 1/2 and the deny override below is the only way to shipping_sold_out. */
function resolveDeliveryEstimate(input) {
  const {
    todayISO,
    nowMinutes,
    cutoffMinutes,
    holidays,
    warehouseId,
    incoming,
    leadTimeDays,
    deliveryDays,
    inventoryPolicy,
    largestSourceQuantity,
    storeQuantities,
    // Omitted = no flag, so a caller that does not pass it keeps the comparison alone.
    centrallagerInStock = null,
  } = input;

  const today = deliveryParseISODate(todayISO);
  if (!today) return { shipping: null, pickup: null, warehouseInStock: false };

  const retailInStock = storeQuantities.some((qty) => qty > 0);
  const warehouseInStock = deliveryWarehouseInStock(largestSourceQuantity, storeQuantities, centrallagerInStock);

  // Factory = an upcoming arrival date on the warehouse's incoming entry.
  let factoryDate = null;
  const rawFactoryDate = warehouseId && incoming ? incoming[warehouseId]?.date : null;
  if (rawFactoryDate) {
    const parsed = deliveryParseISODate(rawFactoryDate);
    if (parsed && parsed >= today) factoryDate = parsed;
  }

  const cutoffPickup = () => (nowMinutes < cutoffMinutes ? { key: 'pickup_today' } : { key: 'pickup_tomorrow' });

  // Shipping with the warehouse empty, one helper for rows 1/2 and 4/6 so they cannot drift:
  // the factory arrival +1 working day, else a special order on the AX lead time counted from
  // today +1 working day (the table's "Specialbestilling:" line, 869fatvw3, whatever the
  // Perfion flag says), else null -- nothing to promise, and the metafield fallback card shows.
  const leadTimeBase = Number.isFinite(leadTimeDays) && leadTimeDays >= 0 ? deliveryAddDays(today, leadTimeDays) : null;
  const emptyWarehouseShipping = () => {
    if (factoryDate) return { key: 'shipping_from_date', date: deliveryAddWeekdays(factoryDate, 1, holidays) };
    if (leadTimeBase) return { key: 'shipping_special_order', date: deliveryAddWeekdays(leadTimeBase, 1, holidays) };
    return null;
  };

  let shipping = null;
  let pickup = null;

  if (warehouseInStock) {
    // Rows 3 + 5: warehouse stock — the store's working-day delivery promise (869fatvw3): DK
    // "2–5", SE "from 4", the key chosen per shop in product-delivery-info.liquid; pick-up
    // is the store's next delivery (row 3) or the in-store cutoff promise (row 5).
    shipping = { key: 'shipping_in_stock' };
    if (retailInStock) {
      pickup = cutoffPickup();
    } else {
      let earliest = null;
      for (const value of Object.values(deliveryDays || {})) {
        const date = deliveryParseISODate(value);
        if (date && date >= today && (!earliest || date < earliest)) earliest = date;
      }
      if (earliest) pickup = { key: 'pickup_on_date', date: earliest };
    }
  } else if (retailInStock) {
    // Rows 4 + 6: store stock covers pick-up; shipping is rows 1/2's (ClickUp 869fbkn3h). Stock
    // on a shop floor no longer turns a missing arrival date into "Udsolgt online".
    shipping = emptyWarehouseShipping();
    pickup = cutoffPickup();
  } else if (factoryDate) {
    // Row 1: nothing in stock, factory arrival known.
    shipping = emptyWarehouseShipping();
    pickup = { key: 'pickup_from_date', date: deliveryRollToWorkday(deliveryAddDays(factoryDate, 9), holidays) };
  } else if (leadTimeBase) {
    // Row 2: special order, nothing in stock and no arrival booked.
    shipping = emptyWarehouseShipping();
    pickup = { key: 'pickup_from_date', date: deliveryRollToWorkday(deliveryAddDays(leadTimeBase, 9), holidays) };
  }

  // Overselling off + empty warehouse = sold out online, whichever row the table landed on
  // (ClickUp 869f86cbr) -- and, since 869fbkn3h, the only route to it. With inventory_policy
  // "deny" Shopify refuses to sell beyond stock, so no AX arrival date or lead time can turn
  // into a shipping order, and shop stock is pick-up only -- rows 1/2/4/6 would otherwise
  // print "Afsendes fra <date>" for an order that cannot be placed. Pick-up stays resolved
  // (unused, see DeliveryEstimate). Overselling on, or no policy in the payload, keeps the
  // table as is; rows 3/5 never reach this.
  if (inventoryPolicy === 'deny' && !warehouseInStock) {
    shipping = { key: 'shipping_sold_out' };
  }

  return { shipping, pickup, warehouseInStock };
}

/* "Udsolgt online": the variant cannot be shipped (ClickUp 869eq7r46, 869f86cbr, 869fbkn3h).
   The warehouse holds none and overselling is off -- inventory policy and the warehouse
   verdict decide it, nothing else. Stock standing in the shops does not: with overselling on,
   an empty warehouse ships from the factory date or the AX lead time like any other order.

   Derived, never flagged: no metafield marks a variant discontinued anywhere in the
   catalogue, so the delivery table's own verdict is the definition. Calling the resolver
   keeps that one rule rather than a second copy of it that can drift out of step.

   Holidays, delivery days and the pick-up cutoff are passed inert on purpose. The
   shipping_sold_out verdict is reached on stock and inventory policy alone and never reads
   them, so supplying them would only invite the caller to get them wrong. If
   that verdict ever grows a date dependency, this helper has to start passing them for real. */
function isVariantDiscontinued({
  todayISO,
  warehouseId,
  incoming,
  leadTimeDays,
  inventoryPolicy,
  largestSourceQuantity,
  storeQuantities,
  centrallagerInStock = null,
}) {
  const { shipping } = resolveDeliveryEstimate({
    todayISO,
    nowMinutes: 0,
    cutoffMinutes: 0,
    holidays: new Set(),
    warehouseId,
    incoming,
    leadTimeDays,
    deliveryDays: null,
    inventoryPolicy,
    largestSourceQuantity,
    storeQuantities,
    centrallagerInStock,
  });
  return shipping?.key === 'shipping_sold_out';
}

class DeliveryEstimate extends HTMLElement {
  async connectedCallback() {
    const { shopDomain, storefrontToken, variantGid, warehouseAxId } = this.dataset;
    if (this.dataset.inventoryManaged !== 'true') return;
    if (!shopDomain || !storefrontToken || !variantGid) return;

    const translations = readComponentTranslations(this);
    let payload = null;
    try {
      payload = JSON.parse(this.querySelector('script[data-delivery-data]')?.textContent || 'null');
    } catch {
      return;
    }
    if (!payload) return;

    let storeQuantities = [];
    let liveQuantityAvailable = null;
    let liveCentrallagerInStock = null;
    try {
      const { stores, quantityAvailable, centrallagerInStock } = await fetchVariantAvailabilityCached(
        shopDomain,
        storefrontToken,
        variantGid
      );
      // The element is swapped out on variant change, so a late response may
      // belong to a variant that is no longer on screen.
      if (!this.isConnected) return;
      // quantityAvailable, not `available`: the pick-up promises ("within 4 hours")
      // need physical store stock, not stock that could be transferred in.
      //
      // Only exclude by AX id when one is actually configured. With the setting
      // empty, `store.axLocationId !== ''` would drop every store that is missing
      // its own AX id, quietly counting that store's stock as warehouse stock and
      // promising warehouse delivery for something the warehouse doesn't have.
      storeQuantities = stores
        .filter((store) => !warehouseAxId || store.axLocationId !== warehouseAxId)
        .map((store) => store.quantityAvailable);
      // Read from the same response as storeQuantities above, not the Liquid-rendered
      // dataset snapshot — pairing a live per-store fetch with a page-load-time total
      // let the two drift apart within minutes of a purchase and flip the shown state.
      // The Centrallager flag, from that same response, for the same reason.
      liveQuantityAvailable = quantityAvailable;
      liveCentrallagerInStock = centrallagerInStock;
    } catch {
      const inventoryQuantity = Number(this.dataset.inventoryQuantity) || 0;
      if (inventoryQuantity <= 0) {
        const fallbackCard = this.querySelector('[data-delivery-fallback-card]');
        if (fallbackCard) fallbackCard.hidden = false;
      }
      return;
    }

    const { todayISO, nowMinutes } = deliveryTodayInShopTime();

    // Computed for this year plus the next two. A lead-time chain only ever reaches
    // year+1, but a factory arrival date comes straight from the AX feed with no
    // upper bound — one late-December arrival plus nine days lands in year+2, and a
    // year with no holidays would silently treat a real holiday as a working day.
    // Merchant-entered closures are additive on top.
    const country = this.dataset.country === 'SE' ? 'SE' : 'DK';
    const year = Number(todayISO.slice(0, 4));
    const holidays = new Set([
      ...deliveryNationalHolidays(year, country),
      ...deliveryNationalHolidays(year + 1, country),
      ...deliveryNationalHolidays(year + 2, country),
      ...(Array.isArray(payload.holidays) ? payload.holidays : [])
        .map((value) => String(value).trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value)),
    ]);
    const result = resolveDeliveryEstimate({
      todayISO,
      nowMinutes,
      cutoffMinutes: deliveryParseCutoff(this.dataset.cutoff),
      holidays,
      warehouseId: warehouseAxId || '',
      incoming: payload.incoming || null,
      // The AX feed has a history of shipping numbers as strings, so coerce rather
      // than type-check — but never let null/'' become Number(0), which would read
      // as "no lead time at all" and promise shipping tomorrow.
      leadTimeDays: deliveryParseLeadTime(payload.leadTimeDays),
      deliveryDays: payload.deliveryDays || null,
      inventoryPolicy: payload.inventoryPolicy,
      largestSourceQuantity: liveQuantityAvailable,
      storeQuantities,
      centrallagerInStock: liveCentrallagerInStock,
    });

    // The verdict, on the element: read by the special-order USP rule in
    // product-info-ilva.css (ClickUp 869fe2ef6), which hides "Ubegrænset returret" next to it.
    this.dataset.shippingKey = result.shipping?.key || '';

    // Only the shipping half is rendered. The pick-up half moved into the store drawer
    // in 869ebk2gn and was dropped from there in 869f1gmnb, so result.pickup is still
    // resolved but deliberately unused — no surface shows a pick-up date any more.
    //
    // "Udsolgt online" gets no delivery card at all (ClickUp 869eq7r46, round 3): a delivery box
    // that promises no delivery is noise, and the greyed-out CTA already carries the words.
    // Skipping applyLine leaves the card at the `hidden` Liquid rendered it with, which the
    // [hidden] rule in product-info-ilva.css collapses out of the flex column gap and all.
    if (result.shipping?.key !== 'shipping_sold_out') {
      this.applyLine(result.shipping, translations, '[data-delivery-shipping-text]', '[data-delivery-shipping-card]');
    }

    // When AX data is stale or invalid, show the fallback metafield card if available.
    if (!result.shipping) {
      const fallbackCard = this.querySelector('[data-delivery-fallback-card]');
      if (fallbackCard) fallbackCard.hidden = false;
    }
  }

  applyLine(line, translations, textSelector, wrapperSelector) {
    if (!line) return;
    const template = translations[line.key];
    const textEl = this.querySelector(textSelector);
    const wrapperEl = this.querySelector(wrapperSelector);
    if (!template || !textEl || !wrapperEl) return;
    textEl.textContent = line.date ? template.replace('__date__', deliveryFormatDate(line.date)) : template;

    wrapperEl.hidden = false;
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}

/* The CTAs the gate below governs, named one by one rather than caught with a blanket
   button[name="add"]. The product section also hosts recommendation and complementary
   cards, and their add buttons belong to entirely different products that must stay live. */
const PURCHASABILITY_CTA_SELECTOR = [
  '[data-dynamic-variant-content$="-add-to-cart"] button[name="add"]', // PDP + quick-add drawer
  'sticky-atc-panel button[name="add"]', // sticky panel's mini card
].join(', ');

/* Greys out every purchase CTA for a variant that can no longer be shipped (ClickUp 869eq7r46):
   warehouse empty and overselling off (869f86cbr, 869fbkn3h -- isVariantDiscontinued).

   Shopify still counts these variants as available, because the aggregate inventory number
   includes the stock standing in the shops -- so nothing in Liquid can tell the buy buttons
   to stand down. The verdict needs per-location quantities, which only the Storefront API
   carries, so the gate resolves asynchronously and the CTAs start out enabled. On the PDP
   that request is already in flight for the delivery card and the store drawer, and
   fetchVariantAvailabilityCached hands all three the same promise.

   Lifecycle copies <delivery-estimate>: the element sits inside a data-dynamic-variant-content
   wrapper, so a variant change destroys it and builds a new one carrying that variant's data.
   No stale AX payload, and no all-variants blob in the page HTML.

   The one thing that lifecycle cannot handle on its own is variant-picker.js, which re-enables
   and relabels the add button from updateAddToCartButton() on a 10ms timer after each change --
   later than this element's own await resolves. Re-applying on on:variant:change, which
   variant-picker announces after that timer, is what makes the state stick. */
class PurchasabilityGate extends HTMLElement {
  connectedCallback() {
    if (!this.boundVariantChange) this.boundVariantChange = this.handleVariantChange.bind(this);
    document.addEventListener('on:variant:change', this.boundVariantChange);
    this.resolveState();
  }

  disconnectedCallback() {
    document.removeEventListener('on:variant:change', this.boundVariantChange);
  }

  handleVariantChange() {
    if (this.discontinued) this.applyState();
  }

  async resolveState() {
    const { shopDomain, storefrontToken, variantGid, warehouseAxId } = this.dataset;
    if (!shopDomain || !storefrontToken || !variantGid) return;

    let payload = null;
    try {
      payload = JSON.parse(this.querySelector('script[data-gate-data]')?.textContent || 'null');
    } catch {
      return;
    }
    if (!payload) return;

    let storeQuantities = [];
    let liveQuantityAvailable = null;
    let liveCentrallagerInStock = null;
    try {
      const { stores, quantityAvailable, centrallagerInStock } = await fetchVariantAvailabilityCached(
        shopDomain,
        storefrontToken,
        variantGid
      );
      if (!this.isConnected) return;
      // Same exclusion rule as <delivery-estimate>: drop the warehouse's own location only
      // when an id is actually configured. Without that guard every store missing an AX id
      // would be counted as warehouse stock, and no variant would ever read as discontinued.
      storeQuantities = stores
        .filter((store) => !warehouseAxId || store.axLocationId !== warehouseAxId)
        .map((store) => store.quantityAvailable);
      // Same response as storeQuantities, not the Liquid-rendered dataset snapshot —
      // see <delivery-estimate> for why mixing the two sources is what caused the
      // post-purchase state flip this fixes. Same for the Centrallager flag.
      liveQuantityAvailable = quantityAvailable;
      liveCentrallagerInStock = centrallagerInStock;
    } catch {
      // No answer is not evidence of a discontinued variant. Leaving the CTAs alone is the
      // safe failure: someone may buy a thing that is slow to ship, which support can sort
      // out, whereas blocking a healthy product is lost revenue that nobody reports.
      return;
    }

    this.discontinued = isVariantDiscontinued({
      todayISO: deliveryTodayInShopTime().todayISO,
      warehouseId: warehouseAxId || '',
      incoming: payload.incoming || null,
      leadTimeDays: deliveryParseLeadTime(payload.leadTimeDays),
      inventoryPolicy: payload.inventoryPolicy,
      largestSourceQuantity: liveQuantityAvailable,
      storeQuantities,
      centrallagerInStock: liveCentrallagerInStock,
    });
    if (this.discontinued) this.applyState();
  }

  applyState() {
    const scopeSelector = this.dataset.scope;
    const root = (scopeSelector && this.closest(scopeSelector)) || document;
    const label = this.dataset.label;

    root.querySelectorAll(PURCHASABILITY_CTA_SELECTOR).forEach((button) => {
      button.disabled = true;
      if (!label) return;
      // Icon-only CTAs (the sticky panel's mini card) keep their icon and carry their label
      // in a visually-hidden span; the plain-text ones own their whole text node. Writing a
      // label is always safe -- ilva-button-roll.js observes the change and re-wraps it --
      // but reading one back is not, since the roll leaves two copies of the text behind.
      const srLabel = button.querySelector('.visually-hidden');
      if (srLabel) {
        srLabel.textContent = label;
      } else {
        button.textContent = label;
      }
    });

    // Shopify owns the markup inside the dynamic-checkout wrapper and its buttons cannot be
    // reliably disabled, so the wrapper is hidden instead -- the same move the zero-price gate
    // makes (task 869eba8mv). Left visible, it walks the shopper straight past the grey button.
    root.querySelectorAll('.product-info__dynamic-checkout-button').forEach((el) => {
      el.hidden = true;
    });
  }
}

if (!customElements.get('purchasability-gate')) {
  customElements.define('purchasability-gate', PurchasabilityGate);
}

/* --- StoreLocatorPage Web Component (Standalone) --- */

class StoreLocatorPage extends HTMLElement {
  connectedCallback() {
    this.config = {
      shopDomain: this.dataset.shopDomain,
      token: this.dataset.storefrontToken,
      mapApiKey: this.dataset.mapApiKey,
      mapProvider: this.dataset.mapProvider || 'mapbox',
      markerColor: this.dataset.markerColor || '#000000',
    };

    this.translations = readComponentTranslations(this);

    this.stores = [];
    this.filteredStores = [];
    this.mapAdapter = null;

    this.listEl = this.querySelector('[data-page-store-list]');
    this.loadingEl = this.querySelector('[data-page-loading]');
    this.emptyEl = this.querySelector('[data-page-empty]');
    this.errorEl = this.querySelector('[data-page-error]');
    this.locationErrorEl = this.querySelector('[data-page-location-error]');
    this.mapContainer = this.querySelector('[data-page-map]');
    this.searchInput = this.querySelector('[data-page-search-input]');
    this.searchBtn = this.querySelector('[data-page-search-btn]');
    this.searchClear = this.querySelector('[data-page-search-clear]');
    this.popupOverlay = this.querySelector('[data-page-popup-overlay]');
    this.outletToggle = this.querySelector('[data-page-outlet-toggle]');

    this.initSearch();
    this.useLocationBtn = this.querySelector('[data-page-use-my-location]');
    this.useLocationBtn?.addEventListener('click', () => this.handleUseMyLocation());
    this.outletToggle?.addEventListener('click', () => {
      const active = this.outletToggle.getAttribute('aria-checked') === 'true';
      this.outletToggle.setAttribute('aria-checked', String(!active));
      this.applyFilters();
    });
    this.popupOverlay?.addEventListener('click', (e) => {
      if (e.target === this.popupOverlay || e.target.closest('[data-close-popup]')) {
        this.closePopup();
      }
    });

    this.loadData();

    const initialQuery = new URLSearchParams(window.location.search).get('q');
    if (initialQuery && this.searchInput) {
      this.searchInput.value = initialQuery;
      this.toggleClearButton();
    }
  }

  async loadData() {
    if (!this.config.token || !this.config.shopDomain) {
      this.showError(this.translations.error_loading);
      return;
    }

    this.showLoading();
    try {
      const client = new StorefrontClient(this.config.shopDomain, this.config.token);
      this.stores = await client.fetchLocations();
      this.stores.sort((a, b) => a.name.localeCompare(b.name, 'da'));
      this.applyFilters();
      await this.initMap();

      if (this.searchInput?.value?.trim()) {
        this.handleSearch();
      }
    } catch (err) {
      console.error('Store locator page load error:', err);
      this.showError(this.translations.error_loading);
    }
  }

  applyFilters() {
    if (this._searchError) return;
    let stores = [...this.stores];
    if (this.outletToggle?.getAttribute('aria-checked') === 'true') {
      stores = stores.filter((s) => s.isOutlet);
    }
    this.filteredStores = stores;
    this.renderList();
    this.updateMapMarkers(true);
  }

  renderList() {
    if (!this.listEl) return;
    if (this.filteredStores.length === 0) {
      this.listEl.innerHTML = '';
      this.showEmpty();
      return;
    }
    this.hideStates();
    this.listEl.innerHTML = this.filteredStores
      .map((store) => {
        const storeStatus = getStoreStatus(store.openingHours, this.translations);
        const STATUS_WORD_MAP = { open: 'status_open', closing_soon: 'closing_soon', opening_soon: 'opening_soon', closed: 'status_closed' };
        const statusWord = this.translations[STATUS_WORD_MAP[storeStatus.status]] || '';
        const detailHtml = storeStatus.detail ? ` – ${storeStatus.detail}` : '';
        const statusHtml = `<span class="text-sm"><span class="store-locator__status-text store-locator__status-text--${storeStatus.status}">${statusWord}</span>${detailHtml}</span>`;
        const address = renderAddress(store.address);

        const storeLink = store.pageUrl
          ? `<a href="${store.pageUrl}" class="store-locator-page__store-link text-sm">${this.translations.store_page_link || ''}</a>`
          : '';

        return `
          <li data-store-id="${store.id}">
            <div class="store-locator-page__store-name">${store.name}</div>
            <div class="store-locator-page__store-address text-body-small">${address}</div>
            <div class="store-locator-page__store-meta">
              ${statusHtml}
              ${storeLink}
            </div>
          </li>
        `;
      })
      .join('');

    this.listEl.querySelectorAll('li').forEach((li) => {
      li.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        const store = this.filteredStores.find((s) => s.id === li.dataset.storeId);
        if (!store) return;
        this.highlightListItem(store.id);
        if (store.lat && store.lng && this.mapAdapter) {
          this.mapAdapter.fitBounds([store]);
        }
        this.openPopup(store);
      });
    });
  }

  async initMap() {
    if (!this.mapContainer || !this.config.mapApiKey) return;
    this.mapAdapter = createMapAdapter(this.config.mapProvider, this.config.mapApiKey);
    await this.mapAdapter.initMap(this.mapContainer, { lat: 56.15, lng: 10.2, zoom: 7 });
    this.updateMapMarkers(true);
  }

  updateMapMarkers(fitAll = false) {
    if (!this.mapAdapter) return;
    this.mapAdapter.clearMarkers();
    const withCoords = this.filteredStores.filter((s) => s.lat && s.lng);
    for (const store of withCoords) {
      this.mapAdapter.addMarker(store.lat, store.lng, store, (s) => {
        this.highlightListItem(s.id);
        if (s.lat && s.lng) this.mapAdapter.fitBounds([s]);
        this.openPopup(s);
      }, { color: this.config.markerColor });
    }
    if (fitAll) this.mapAdapter.fitBounds(withCoords);
  }

  openPopup(store) {
    if (!this.popupOverlay) return;
    // GA4: the popup IS the address + opening hours view (s360 spec). No
    // variant on this page, so stock/display params resolve to empty strings.
    window.ilvaTrack?.('address_and_opening_hours', dlStoreParams(store));
    this.popupOverlay.innerHTML = renderPopupCard(store, this.translations, { directionsEvent: 'route_directions_find_store_page' });
    this.popupOverlay.classList.add('is-active');
    this.popupOverlay.querySelector('[data-close-popup]')?.addEventListener('click', () => this.closePopup());
  }

  closePopup() {
    this.popupOverlay?.classList.remove('is-active');
  }

  highlightListItem(storeId) {
    if (!this.listEl) return;
    this.listEl.querySelectorAll('li.is-active').forEach((el) => el.classList.remove('is-active'));
    const target = this.listEl.querySelector(`li[data-store-id="${storeId}"]`);
    if (target) {
      target.classList.add('is-active');
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  applySearchCoords(coords) {
    this._searchError = false;
    this.closePopup();
    this.hideStates();
    this.sortByDistance(coords);
    this.applyFilters();
    if (this.mapAdapter) {
      const closest = this.filteredStores.filter((s) => s.lat && s.lng).slice(0, 3);
      this.mapAdapter.smartZoom(coords, closest);
    }
  }

  resetComponentState() {
    this._searchError = false;
    this.closePopup();
    this.applyFilters();
  }

  onSearchNoResults(query) {
    this._searchError = true;
    const msg = (this.translations.search_no_results || '').replace('__QUERY__', query);
    if (this.listEl) this.listEl.innerHTML = '';
    this.showError(msg);
  }

  updateUrlParam(query) {
    const url = new URL(window.location);
    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    window.history.replaceState({}, '', url);
  }

  showLoading() {
    this.hideStates();
    if (this.loadingEl) this.loadingEl.style.display = '';
  }

  showEmpty() {
    this.hideStates();
    if (this.emptyEl) this.emptyEl.style.display = '';
  }

  showError(msg) {
    this.hideStates();
    if (this.errorEl) {
      this.errorEl.textContent = msg || '';
      this.errorEl.style.display = '';
    }
  }

  /* Geolocation errors get their own slot in the header, next to the button that
     triggered them, and are deliberately independent of hideStates() — the
     result list keeps whatever it was showing. Writing into the live region is
     what makes it announce, and CSS hides the element while it is empty. */
  showLocationError(msg) {
    if (this.locationErrorEl) this.locationErrorEl.textContent = msg || '';
  }

  hideLocationError() {
    if (this.locationErrorEl) this.locationErrorEl.textContent = '';
  }

  hideStates() {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.emptyEl) this.emptyEl.style.display = 'none';
    if (this.errorEl) this.errorEl.style.display = 'none';
  }
}

Object.assign(StoreLocatorPage.prototype, SearchMixin);

if (!customElements.get('store-locator-page')) {
  customElements.define('store-locator-page', StoreLocatorPage);
}

// Exposed for product-configurator.js's fabric modal, which needs the same batched
// warehouse verdict but isn't itself a store-locator consumer. main-product.liquid
// loads this script (deferred) ahead of the configurator's, so it has run by the time
// the modal can open; the configurator still checks for it before calling.
window.fetchVariantsAvailabilityCached = fetchVariantsAvailabilityCached;

/* store-page.js runs as a separate classic script and can't reach the
   block-scoped declarations above, so the shared API is published on the
   theme namespace (same pattern as theme.quickBuy in quick-add.js). */
window.theme = window.theme || {};
window.theme.storeLocator = {
  StorefrontClient,
  createMapAdapter,
  readComponentTranslations,
  hoursRows,
  renderAddress,
  buildDirectionsUrl,
  escapeHtml,
};

}
